import type { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { color, diagramStyle } from './palette';

/**
 * The claim row: one source stating one fact at one grain as of one date on one basis. Three claims of the same fact
 * sit side by side in Silver, and Gold shows one of them: the best source first, and of its claims the newest to land.
 */
export const ClaimRow: FC = () => {
  const theme = useTheme();
  const sub = { fill: color.muted, fontSize: 13 } as const;
  const row = { fontSize: 14, fill: color.fg } as const;
  const columns: Array<[number, string]> = [
    [40, 'source'],
    [190, 'fact'],
    [370, 'grain'],
    [540, 'as-of date'],
    [660, 'basis'],
    [790, 'priority · landed'],
  ];
  const claims: Array<{ cells: string[]; winner: boolean }> = [
    { cells: ['fund admin', 'NAV · 412.0m', 'Fund III', '30 Jun', 'reported', '1 · 9 Jul'], winner: true },
    { cells: ['fund admin', 'NAV · 408.0m', 'Fund III', '30 Jun', 'reported', '1 · 2 Jul'], winner: false },
    { cells: ['GP sheet', 'NAV · 410.5m', 'Fund III', '30 Jun', 'estimate', '2 · 3 Jul'], winner: false },
  ];

  return (
    <svg viewBox="0 0 940 310" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="The claim row" style={diagramStyle(theme)}>
      <defs>
        <marker id="academy-arrow-claim" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill={color.muted} />
        </marker>
      </defs>
      <text x="40" y="22" fontSize="14" fontWeight="600" fill={color.silver}>
        Silver · three claims of one fact
      </text>
      {columns.map(([x, name]) => (
        <text key={name} x={x + 12} y="44" style={sub}>
          {name}
        </text>
      ))}
      {claims.map(({ cells, winner }, i) => {
        const y = 54 + i * 38;
        return (
          <g key={cells[5]}>
            <rect x="40" y={y} width="860" height="32" rx="4" fill={winner ? color.gold : color.silver} opacity={winner ? 0.25 : 0.14} />
            {cells.map((cell, c) => (
              <text key={columns[c]![1]} x={columns[c]![0] + 12} y={y + 21} style={row} fontWeight={winner ? 600 : 400}>
                {cell}
              </text>
            ))}
            {winner && (
              <text x="890" y={y + 22} textAnchor="end" fontSize="15" fontWeight="700" fill={color.ok}>
                ✓
              </text>
            )}
          </g>
        );
      })}
      <line x1="470" y1="176" x2="470" y2="214" stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-claim)" />
      <text x="484" y="200" style={sub}>
        source priority, then the newest to land
      </text>
      <rect x="240" y="218" width="460" height="72" rx="12" fill={color.gold} fillOpacity=".12" stroke={color.gold} strokeWidth="2.5" />
      <text x="260" y="243" fontSize="14" fontWeight="600" fill={color.gold}>
        Gold shows
      </text>
      <text x="470" y="273" textAnchor="middle" fontSize="15" fontWeight="600" fill={color.fg}>
        Fund III · NAV · 412.0m · fund admin · 30 Jun
      </text>
    </svg>
  );
};
